import { Injectable } from '@angular/core';
import { HttpRequestDecorator } from '../core/triggerbee/http.request.decorator';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import {
  CreateDataSourceRequest,
  DataSourceItem,
  DataSourceModel,
  ExtractDataSourceItemRequest,
  ExtractPropertyDefinitionsRequest,
  PropertyDefinition,
  UpdateDataSourceRequest,
} from './data-sources.models';
import { TriggerbeeApi } from '../core/triggerbee/triggerbee-api.enum';

@Injectable({
  providedIn: 'root',
})
export class DataSourcesService {
  private readonly baseUrl: string;
  private dataSourcesBS: BehaviorSubject<DataSourceModel[]> = new BehaviorSubject<DataSourceModel[]>(undefined);

  constructor(private http: HttpRequestDecorator) {
    this.baseUrl = http.getBaseUrl(TriggerbeeApi.Gateway);
  }

  public get dataSources$(): Observable<DataSourceModel[]> {
    return this.dataSourcesBS.asObservable();
  }

  public get dataSources(): DataSourceModel[] {
    return this.dataSourcesBS.value;
  }

  public loadDataSources(): void {
    this.http.get(this.baseUrl + `/data-sources`).subscribe((dataSources) => {
      this.dataSourcesBS.next(dataSources);
    });
  }

  public getDataSource(id: string): Observable<DataSourceModel> {
    return this.http.get(this.baseUrl + `/data-sources/${id}`);
  }

  public createDataSource(request: CreateDataSourceRequest): Observable<DataSourceModel> {
    return this.http
      .post(this.baseUrl + `/data-sources`, request)
      .pipe(tap((dataSource) => this.dataSourcesBS.next([...this.dataSourcesBS.value, dataSource])));
  }

  public updateDataSource(request: UpdateDataSourceRequest): Observable<DataSourceModel> {
    return this.http.put(this.baseUrl + `/data-sources`, request);
  }

  public deleteDataSource(id: string): Observable<void> {
    return this.http.delete(this.baseUrl + `/data-sources/${id}`);
  }

  public extractPropertyDefinitions(request: ExtractPropertyDefinitionsRequest): Observable<Array<PropertyDefinition>> {
    const queryParams: Record<string, string> = {};

    if (request.url) {
      queryParams.url = request.url;
    }
    if (request.dataSourceType) {
      queryParams.dataSourceType = request.dataSourceType;
    }
    if (request.authenticationType) {
      queryParams.authenticationType = request.authenticationType;
    }
    if (request.authenticationKey) {
      queryParams.authenticationKey = request.authenticationKey;
    }
    if (request.username) {
      queryParams.username = request.username;
    }
    if (request.password) {
      queryParams.password = request.password;
    }
    const queryString = this.buildQueryString(queryParams);

    return this.http.get(this.baseUrl + `/data-sources/property-definitions${queryString}`);
  }

  public extractDataSourceItems(request: ExtractDataSourceItemRequest): Observable<Array<DataSourceItem>> {
    const queryParams: Record<string, string> = {};
    if (request.dataSourceId) {
      queryParams.dataSourceId = request.dataSourceId;
    }
    if (request.descending) {
      queryParams.descending = request.descending.toString();
    }
    if (request.sortBy) {
      queryParams.sortBy = request.sortBy;
    }
    const queryString = this.buildQueryString(queryParams);
    return this.http.get(this.baseUrl + `/data-sources/extract-data-source-items${queryString}`);
  }

  private buildQueryString(params: Record<string, string>): string {
    if (!params || Object.keys(params).length === 0) {
      return '';
    }

    const queryParts = Object.keys(params)
      .filter((key) => params[key] !== undefined && params[key] !== null && params[key] !== '')
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');

    return queryParts ? `?${queryParts}` : '';
  }
}
